import { FORM_UNITS, MEDICATION_FREQUENCY_MAP, TimingCode } from "@/constants/medication-dosage";
import {
  TitrationDecision,
  TitrationDecisionReason,
  TitrationMedByClass,
  VitallCodeSystem,
  VitallExtensionUrl,
  toTitrationDecision,
  toTitrationDecisionReason,
} from "@/constants/titration";
import {
  Dosage,
  DosageValue,
  TitrationPlanEntry,
} from "@/routes/dashboard/components/patient-summary-pages/titration-plan";
import { DATE_FORMAT_ISO } from "@/utils";
import {
  CarePlanStatus,
  GdmtMedicationClass,
  MedicationRequestStatus,
  TitrationCarePlan,
  TitrationMedicationCategory,
} from "@/utils/GraphQLClient/titration";
import { VITALL_DOC_SYSTEM } from "@/utils/fhir-constants";
import { formValueFromLabel, getContainedMedication } from "@/utils/medication-ingredients";

import { fhirR4 } from "@smile-cdr/fhirts";
import dayjs from "dayjs";

import { MED_REQUEST_STATUS_ENUM_TO_FHIR_STATUS } from "../constants/titration";
import { isDevelopment } from "./env";

/**
 * Retrieve the titration medication request's drug class
 * @param medicationRequest Titration medication request
 * @returns If found, the drug class. If not found, undefined
 */
export const extractMedicationRequestDrugClass = (
  medicationRequest: fhirR4.MedicationRequest
): GdmtMedicationClass | undefined => {
  const { category } = medicationRequest;
  const code = (category ?? [])
    .flatMap(({ coding }) => coding ?? [])
    .find(({ code, system }) => code && system === VitallCodeSystem.TitrationDrugClass)?.code;
  return code as GdmtMedicationClass;
};

/**
 * Fixed id for the contained *target* Medication the titration target dose is
 * read off. The current dose rides on the Medication the `medicationReference`
 * names (`#med-rx`); the target dose rides on a second contained Medication
 * (`#med-target`) that nothing references, so it is matched by id.
 */
const TARGET_CONTAINED_MEDICATION_ID = "med-target";

/**
 * Find a contained `Medication` on a resource by its local id, independent of
 * `medicationReference` (which names the current-dose Medication only). Used to
 * locate the unreferenced target-dose Medication (`#med-target`).
 */
const getContainedMedicationById = (
  resource: Pick<fhirR4.MedicationRequest, "contained">,
  id: string
): fhirR4.Medication | undefined => {
  const contained = resource.contained as fhirR4.Medication[] | undefined;
  return contained?.find(entry => entry?.resourceType === "Medication" && entry?.id === id);
};

/**
 * Read a dose off a contained Medication's ingredient strengths.
 *
 * Post-VMD-705 the objective dose ("25 mg") is stored as the strength Ratio on
 * `Medication.ingredient[]`, not on `dosageInstruction.doseAndRate` (which now
 * carries the counted quantity, e.g. "2 tablets") — see
 * MEDICATION_TRANSFORM_RULES.md ("Strength is not quantity"). A combination
 * product states one strength per active ingredient, so each numerator becomes a
 * bound (first → low, second → high), mirroring how `buildIngredientInput`
 * writes them.
 *
 * Returns `undefined` when no ingredient carries a strength, so the caller can
 * fall back to the legacy `doseAndRate` for records written before the move.
 */
const extractStrengthDose = (
  medication: fhirR4.Medication | undefined
): { value: DosageValue; unit: string } | undefined => {
  const numerators = (medication?.ingredient ?? [])
    .map(({ strength }) => strength?.numerator)
    .filter((numerator): numerator is fhirR4.Quantity => numerator?.value !== undefined);
  if (numerators.length === 0) {
    return undefined;
  }
  const [low, high] = numerators;
  return {
    value: { low: low?.value, high: high?.value },
    unit: low?.unit ?? high?.unit ?? "",
  };
};

/** True when the unit names a dose form the Form select can hold (`FORM_UNITS`). */
const isFormUnit = (unit?: string): boolean => !!unit && FORM_UNITS.some(option => option.value === unit);

/**
 * The entry's dose form, restricted to `FORM_UNITS` — the only values the Form
 * select can hold. The counted-dose noun (`doseQuantity.unit`) is preferred, then
 * the contained `Medication.form.text`, then the strength denominator unit off the
 * contained Medication.
 *
 * `form.text` is a human-readable label ("Tablet", "tablet(s)"), not an option
 * value, so it is normalised through `formValueFromLabel` before the `isFormUnit`
 * gate — an exact match would reject every spelling but the canonical singular and
 * leave `form` empty. `doseQuantity.unit` and the denominator are already stored as
 * option values ("tablet", "mL"), so they are matched directly.
 *
 * A legacy record can carry a *strength* unit ("mg") on `doseQuantity` from before
 * the strength/quantity split (VMD-705). That is not a dose form, so it is
 * rejected rather than leaked into `form`: the Form select renders no MenuItem for
 * it (blank, no `unitOptionsFor` backstop here) and a save would emit a nonsense
 * "per 1 mg" strength denominator (`isNamedForm("mg")` is true). Falling through
 * to `""` keeps the field honestly empty until a real form is chosen.
 */
const resolveEntryForm = (
  doseQuantityUnit: string | undefined,
  medicationRequest: fhirR4.MedicationRequest
): string => {
  const containedMedication = getContainedMedication(medicationRequest);
  const formText = formValueFromLabel(containedMedication?.form?.text);
  const denominatorUnit = containedMedication?.ingredient?.find(({ strength }) => strength?.denominator?.unit)?.strength
    ?.denominator?.unit;
  return [doseQuantityUnit, formText, denominatorUnit].find(isFormUnit) ?? "";
};

/**
 * Convert a single medication request into the med req entry form state
 * @param medicationRequest One medication request
 * @returns Entry used in form
 */
export const medicationRequestToEntry = (
  medicationRequest: fhirR4.MedicationRequest | undefined
): TitrationPlanEntry => {
  if (!medicationRequest) {
    return createEmptyEntry();
  }

  const {
    dispenseRequest,
    dosageInstruction: _dosageInstruction,
    extension,
    medicationReference,
    note,
    status,
  } = medicationRequest;
  const [dosageInstruction] = _dosageInstruction ?? [];

  // The current dose ("25 mg") lives on the contained Medication's
  // `ingredient[].strength` (VMD-705). Prefer it, falling back to the legacy
  // `dosageInstruction.doseAndRate` for records written before the strength moved
  // off the dosage — see MEDICATION_TRANSFORM_RULES.md ("Strength is not quantity").
  const strengthDose = extractStrengthDose(getContainedMedication(medicationRequest));
  const doseAndRate = dosageInstruction?.doseAndRate?.[0];
  const doseRange = doseAndRate?.doseRange;
  const doseQuantity = doseAndRate?.doseQuantity;

  // Prefer doseRange (e.g. combination drugs with distinct low/high), fall back to doseQuantity
  const currentDoseValue: DosageValue =
    strengthDose?.value ??
    (doseRange ? { low: doseRange.low?.value, high: doseRange.high?.value } : { low: doseQuantity?.value });
  const currentDoseUnit =
    strengthDose?.unit ??
    (doseRange ? (doseRange.low?.unit ?? doseRange.high?.unit ?? "") : (doseQuantity?.unit ?? ""));

  const timing = dosageInstruction?.timing?.repeat;
  const mappedFrequency = timing
    ? (MEDICATION_FREQUENCY_MAP[timing?.frequency ?? 0]?.[timing?.period ?? 0]?.[timing?.periodUnit ?? "d"] ?? "custom")
    : "custom";
  // Any frequency that is not (1/2/3/4)x/day will be displayed as 'Custom'
  const frequency =
    mappedFrequency === "once_daily" ||
    mappedFrequency === "twice_daily" ||
    mappedFrequency === "three_times_daily" ||
    mappedFrequency == "four_times_daily"
      ? mappedFrequency
      : "custom";

  const currentDosage: Dosage = {
    value: currentDoseValue,
    unit: currentDoseUnit,
    frequency,
    // TODO: Find a custom med request to fill these values
    frequencyTimes: "",
    frequencyEvery: "",
    frequencyUnit: "",
    timing: (dosageInstruction?.timing?.repeat?.when as TimingCode[]) ?? [],
  };

  // The target dose value ("25 mg") rides on a second contained Medication
  // (`#med-target`) ingredient strength, mirroring how the current dose rides on
  // `#med-rx` (VMD-705, "Strength is not quantity"). The request carries a single
  // `dosageInstruction`, so the target shares the current dose's schedule
  // (frequency/timing) — otherwise the target's frequency renders blank on the
  // card. With no target Medication there is nothing to titrate toward, so the
  // target dosage stays empty.
  const targetStrengthDose = extractStrengthDose(
    getContainedMedicationById(medicationRequest, TARGET_CONTAINED_MEDICATION_ID)
  );
  const targetDosage: Dosage = targetStrengthDose
    ? {
        ...emptyDosageFields,
        value: targetStrengthDose.value,
        unit: targetStrengthDose.unit,
        frequency,
        timing: currentDosage.timing,
      }
    : emptyDosageFields;

  const { decision, decisionReasons } = (extension ?? []).reduce<
    Pick<TitrationPlanEntry, "decision" | "decisionReasons">
  >(
    (acc, { url, valueCodeableConcept }) => {
      switch (url) {
        case VitallExtensionUrl.TitrationDecision:
          (valueCodeableConcept?.coding ?? []).forEach(({ code }) => {
            const decision = toTitrationDecision(code);
            if (decision) {
              acc.decision = decision;
            }
          });
          return acc;
        case VitallExtensionUrl.TitrationDecisionReason:
          (valueCodeableConcept?.coding ?? []).forEach(({ code }) => {
            const reason = toTitrationDecisionReason(code);
            if (reason && !acc.decisionReasons.includes(reason)) {
              acc.decisionReasons.push(reason);
            }
          });
          return acc;
        default:
          return acc;
      }
    },
    {
      decision: undefined,
      decisionReasons: [],
    }
  );

  const containedMedication = getContainedMedication(medicationRequest);

  return {
    collapsed: false,
    status: isMedicationRequestStatus(status) ? MED_REQUEST_STATUS_ENUM_TO_FHIR_STATUS[status] : "empty",
    // `medicationId` is the *source MedicationStatement* id (it flows into
    // `source_medication_statement_id` via `entryToMedicationInput`). A loaded
    // MedicationRequest carries no reference back to that statement, so leave it
    // empty rather than using the MedicationRequest's own id — the caller sets
    // the real source id from `medicationStatementMap` when one exists.
    medicationId: "",
    medicationName:
      medicationReference?.display ??
      (() => {
        const itemCodeableConcept = containedMedication?.ingredient?.[0]?.itemCodeableConcept;
        return itemCodeableConcept?.coding?.[0]?.display ?? itemCodeableConcept?.text ?? "";
      })(),
    medicationCategory: undefined,
    route: dosageInstruction?.route?.text ?? "",
    dateStarted: dispenseRequest?.validityPeriod?.start
      ? dayjs(dispenseRequest.validityPeriod.start).format(DATE_FORMAT_ISO)
      : "",
    reason: note?.map(({ text }) => text).join(", ") ?? "",
    decision,
    decisionReasons,
    currentDosage,
    targetDosage,
    newDosage: currentDosage,
    // Prefer MedicationRequest.dosageInstruction[].doseAndRate.doseQuantity.unit,
    // then MedicationRequest.contained[].form.text,
    // then MedicationRequest.contained[].ingredient[].strength.denominator.unit —
    // but only when the value is an actual dose form (see resolveEntryForm).
    form: resolveEntryForm(doseQuantity?.unit, medicationRequest),
    quantity: strengthDose ? doseQuantity?.value : undefined,
  };
};

export const createDefaultMedRequestMap = (): Map<GdmtMedicationClass, fhirR4.MedicationRequest | undefined> =>
  new Map([
    [GdmtMedicationClass.BetaBlocker, undefined],
    [GdmtMedicationClass.Rasi, undefined],
    [GdmtMedicationClass.Sglt2i, undefined],
    [GdmtMedicationClass.Mra, undefined],
  ]);

export const createDefaultMedStatementMap = (): Map<GdmtMedicationClass, fhirR4.MedicationStatement | undefined> =>
  new Map([
    [GdmtMedicationClass.BetaBlocker, undefined],
    [GdmtMedicationClass.Rasi, undefined],
    [GdmtMedicationClass.Sglt2i, undefined],
    [GdmtMedicationClass.Mra, undefined],
  ]);

/**
 * Group MedReqs into their class and transform them into form values
 * @param medRequests Array of titration medication requests
 * @returns MedReq's grouped by their class:
 * - formState has their MedReqs transformed to be form values
 * - originalState has their MedReqs categorized only
 */
export const categorizeMedRequests = (medRequests?: TitrationCarePlan["medicationRequests"]) => {
  return (medRequests ?? []).reduce<{
    // Form state will always have an empty entry if there is no actual med request with that class
    formState: Record<GdmtMedicationClass, TitrationPlanEntry>;
    // FHIR medication requests mapped - if there is no med req for a class, it will be undefined
    originalState: Map<GdmtMedicationClass, fhirR4.MedicationRequest | undefined>;
  }>(
    (acc, medReq) => {
      const code = extractMedicationRequestDrugClass(medReq);
      if (!code) {
        return acc;
      }
      acc.formState[code] = medicationRequestToEntry(medReq);
      acc.originalState.set(code, medReq);
      return acc;
    },
    { formState: createEmptyStateMap(), originalState: createDefaultMedRequestMap() }
  );
};

/**
 * Resolve a MedicationStatement's GDMT titration drug class from its Vitall category code.
 * Titration membership is derived from the drug class (not the med's status), so this stays
 * valid for on-hold/stopped meds that remain part of an active plan.
 * @param medStatement A patient MedicationStatement resource
 * @returns The mapped `GdmtMedicationClass`, or `undefined` when the category isn't a GDMT class
 */
export const getMedicationStatementDrugClass = (
  medStatement: fhirR4.MedicationStatement
): GdmtMedicationClass | undefined => {
  const categoryCode = medStatement.category?.coding?.find(({ system }) => system === VITALL_DOC_SYSTEM)?.code;
  switch (categoryCode) {
    case "betaBlockers":
      return GdmtMedicationClass.BetaBlocker;
    case "mras":
      return GdmtMedicationClass.Mra;
    case "sglt2i":
      return GdmtMedicationClass.Sglt2i;
    case "acei":
    case "arbs":
    case "arnis":
      return GdmtMedicationClass.Rasi;
    default:
      return undefined;
  }
};

/** The RASi sub-classes selectable as an entry's `medicationCategory` (acei/arbs/arnis). */
const RASI_SUBCLASS_CODES = [
  TitrationMedicationCategory.acei,
  TitrationMedicationCategory.arbs,
  TitrationMedicationCategory.arnis,
];

/**
 * Resolve a MedicationStatement's RASi sub-class (acei/arbs/arnis) from its Vitall
 * category coding. Only the RASi sub-classes are returned; the drug-class codes
 * (betaBlockers/mras/sglt2i) aren't `medicationCategory` values and map to `undefined`.
 *
 * The sub-class lives on the source MedicationStatement, not the MedicationRequest, so
 * this is how a subsequent titration round recovers it: the round's decision form hides
 * the sub-class dropdown, but the value must still reach `medication_statement_class` in
 * the save payload.
 * @param medStatement A patient MedicationStatement resource
 * @returns The RASi sub-class, or `undefined` when the statement carries no sub-class code
 */
export const getMedicationStatementSubClass = (
  medStatement: fhirR4.MedicationStatement | undefined
): TitrationMedicationCategory | undefined => {
  const code = medStatement?.category?.coding?.find(({ system }) => system === VITALL_DOC_SYSTEM)?.code;
  return RASI_SUBCLASS_CODES.find(subClass => subClass === code);
};

/**
 * Seed each form entry's `medicationCategory` from its source MedicationStatement's RASi
 * sub-class. Called after the plan loads (and after discarding edits) so a subsequent
 * round carries the sub-class forward into the save payload without re-showing the dropdown.
 * Entries whose statement has no sub-class are left untouched.
 * @param formState The categorized form state, keyed by GDMT class
 * @param medStatementMap Source MedicationStatements keyed by GDMT class
 * @returns A new form-state map with `medicationCategory` populated where available
 */
export const seedMedicationCategories = (
  formState: Record<GdmtMedicationClass, TitrationPlanEntry>,
  medStatementMap: Map<GdmtMedicationClass, fhirR4.MedicationStatement | undefined>
): Record<GdmtMedicationClass, TitrationPlanEntry> => {
  const seeded = { ...formState };
  (Object.keys(seeded) as GdmtMedicationClass[]).forEach(medClass => {
    const subClass = getMedicationStatementSubClass(medStatementMap.get(medClass));
    if (subClass) {
      seeded[medClass] = { ...seeded[medClass], medicationCategory: subClass };
    }
  });
  return seeded;
};

/**
 * Group MedicationStatement resources by GdmtMedicationClass enum
 * @param medStatements Titration MedicationStatement resources
 * @returns Map object, where key is titration drug class and value is full MedStat
 */
export const categorizeMedStatements = (medStatements?: TitrationCarePlan["medicationStatements"]) => {
  return (medStatements ?? []).reduce<Map<GdmtMedicationClass, fhirR4.MedicationStatement | undefined>>((acc, ms) => {
    const drugClass = getMedicationStatementDrugClass(ms);
    if (!drugClass) {
      const categoryCode = ms.category?.coding?.find(({ system }) => system === VITALL_DOC_SYSTEM)?.code;
      if (categoryCode && isDevelopment) {
        console.error(`MedStatement titration category ${categoryCode} isn't supported`);
      }
      return acc;
    }
    const incomingStatus = ms.status;
    if (acc.get(drugClass) && incomingStatus === "stopped") {
      // Incoming is stopped and we already have a statement for this class — keep the existing one
      return acc;
    }
    acc.set(drugClass, ms);
    return acc;
  }, createDefaultMedStatementMap());
};

/**
 * Type guard that narrows a string to a `MedicationRequestStatus`.
 * @param value The status string to check (e.g. from a fetched MedicationRequest)
 * @returns `true` if `value` is one of the `MedicationRequestStatus` enum values, narrowing its type
 */
export const isMedicationRequestStatus = (value: string): value is MedicationRequestStatus =>
  Object.values(MedicationRequestStatus).includes(value as MedicationRequestStatus);

/**
 * Type guard that narrows a string to a `CarePlanStatus`.
 * @param value The status string to check (e.g. from a fetched CarePlan)
 * @returns `true` if `value` is one of the `CarePlan` enum values, narrowing its type
 */
export const isCarePlanStatus = (value: string): value is CarePlanStatus =>
  Object.values(CarePlanStatus).includes(value as CarePlanStatus);

/**
 * Type guard that narrows a string to a `GdmtMedicationClass`.
 * @param value The class code to check (e.g. from a MedicationRequest category coding)
 * @returns `true` if `value` is one of the `GdmtMedicationClass` enum values, narrowing its type
 */
export const isGdmtMedicationClass = (value: string): value is GdmtMedicationClass =>
  Object.values(GdmtMedicationClass).includes(value as GdmtMedicationClass);

export const emptyDosageFields: Dosage = {
  value: {},
  unit: "mg",
  frequency: "",
  frequencyTimes: "",
  frequencyEvery: "",
  frequencyUnit: "",
  timing: [],
};

export const createEmptyFormFields = (): Omit<TitrationPlanEntry, "status" | "collapsed"> => ({
  medicationId: "",
  medicationName: "",
  medicationCategory: undefined,
  route: "",
  dateStarted: "",
  reason: "",
  decision: undefined,
  decisionReasons: [],
  currentDosage: emptyDosageFields,
  targetDosage: emptyDosageFields,
  newDosage: emptyDosageFields,
  form: "",
  quantity: undefined,
});

export const createEmptyEntry = (overrides: Partial<TitrationPlanEntry> = {}): TitrationPlanEntry => ({
  status: "empty",
  collapsed: false,
  ...createEmptyFormFields(),
  ...overrides,
});

export const createEmptyStateMap = (): Record<GdmtMedicationClass, TitrationPlanEntry> => ({
  [GdmtMedicationClass.Rasi]: createEmptyEntry(),
  [GdmtMedicationClass.Sglt2i]: createEmptyEntry(),
  [GdmtMedicationClass.Mra]: createEmptyEntry(),
  [GdmtMedicationClass.BetaBlocker]: createEmptyEntry(),
});

export const emptyByClass = (): TitrationMedByClass =>
  Object.values(GdmtMedicationClass).reduce((acc, cls) => {
    acc[cls] = [];
    return acc;
  }, {} as TitrationMedByClass);

/**
 * @usage Display colour-coded status depending on selected decision in form
 * Determine the MedicationRequest.status field depending on the provided decision
 * @param decision Titration decision enum
 * @returns Corresponding med req status mapped to TitrationDecision. Defaulted to `unknown`
 */
export const getMedicationRequestStatus = (decision?: TitrationDecision) => {
  switch (decision) {
    case TitrationDecision.Increase:
    case TitrationDecision.Decrease:
    case TitrationDecision.Continue:
      return MedicationRequestStatus.active;
    case TitrationDecision.Hold:
      return MedicationRequestStatus.onHold;
    case TitrationDecision.Discontinue:
      return MedicationRequestStatus.stopped;
    default:
      return MedicationRequestStatus.unknown;
  }
};

/**
 * @usage Replacing a discontinued medication
 ** Med is being discontinued & replaced: Med data is in the `replacedMedication` field
 ** Med is only being discontinued: Med data is in the entry itself
 *
 * Determines the decision and decision reasons when a medication is being replaced or not
 * @param entry Form values for GDMT medication class
 * @returns Decision and decision reasons
 */
export const getEffectiveDecision = (
  entry: TitrationPlanEntry
): { decision?: TitrationDecision; decisionReasons: TitrationDecisionReason[]; reason: string } => ({
  decision: entry.replacedMedication?.decision ?? entry.decision,
  decisionReasons: entry.replacedMedication?.decision_reasons ?? entry.decisionReasons,
  reason: entry.isReplacingMedication ? (entry.replacedMedication?.reason_for_titration ?? "") : entry.reason,
});
